import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../../authentication-screens.styles.css';
import { axiosInstance } from '@/services/axios.service';
import { BaseResponse } from '@/types';
import { showSuccess } from '@/modules/notification/engine/notification.library';
import { TextBodyMedium, TextHeadlineExtraLarge } from '@/components/text/Texts';
import Spacing from '@/components/container/Spacing';
import Container from '@/components/container/Container';
import { Card } from 'primereact/card';
import { ENDPOINTS } from '@/constants/endpoints';

export interface VerifyEmailRequest {
	email: string;
	token: string;
}


const EmailVerificationScreen = () => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const [verificationStatus, setVerificationStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
	const [errorMessage, setErrorMessage] = useState('');

	const verifyEmailToken = async () => {
		const token = searchParams.get('token');
		const email = searchParams.get('email');

		if (!token || !email) {
			setVerificationStatus('error');
			setErrorMessage('Invalid verification link. Please check your email or request a new one.');
			return;
		}

		try {
			const response = await axiosInstance.get<BaseResponse>(`${ENDPOINTS['/api/X4bUser/Activate']}?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`);

			if (response.data.Success) {
				setVerificationStatus('success');
				//showSuccess('Email verified successfully!');

				setTimeout(() => {
					navigate('/login');
				}, 5000);
			} else {
				setVerificationStatus('error');
				setErrorMessage(response.data.ErrorMessage || 'Verification failed');
			}
		} catch (error) {
			setVerificationStatus('error');
			setErrorMessage('An error occurred during verification. Please try again.');
		}
	};

	useEffect(() => {

		verifyEmailToken();
	}, []);

	const renderContent = () => {
		switch (verificationStatus) {
			case 'verifying':
				return (
					<>
						<TextHeadlineExtraLarge text="Verifying Your Email" />
						<Spacing height="h-[24px]" />
						<TextBodyMedium text="Please wait while we verify your email address..." />
						<Spacing height="h-[24px]" />
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
					</>
				);

			case 'success':
				return (
					<>
						<div className="text-green-500 text-6xl mb-4">✓</div>
						<TextHeadlineExtraLarge text="Email Verified!" />
						<Spacing height="h-[24px]" />
						<TextBodyMedium text="Your email has been successfully verified. You will be redirected to the login page shortly." />
						<Spacing height="h-[24px]" />
						<TextBodyMedium text="If you're not redirected automatically, click the button below." />
						<Spacing height="h-[24px]" />
						<button
							onClick={() => navigate('/login')}
							className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition-colors"
						>
							Go to Login
						</button>
					</>
				);

			case 'error':
				return (
					<>
						<div className="text-red-500 text-6xl mb-4">✗</div>
						<TextHeadlineExtraLarge text="Verification Failed" />
						<Spacing height="h-[24px]" />
						<TextBodyMedium text={errorMessage} />
						<Spacing height="h-[24px]" />
						<div className="space-y-3">
							<button
								onClick={() => verifyEmailToken()}
								className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition-colors mr-3"
							>
								Try Again
							</button>
							<button
								onClick={() => navigate('/signup')}
								className="bg-secondary text-white px-6 py-2 rounded-lg hover:bg-secondary-dark transition-colors"
							>
								Back to Sign Up
							</button>
						</div>
					</>
				);
		}
	};

	return (
		<Container className="min-h-screen flex items-center justify-center bg-gray-50">
			<Container className="w-full max-w-md">
				<Card className="p-8 text-center">
					{renderContent()}
				</Card>
			</Container>
		</Container>
	);
};

export default EmailVerificationScreen;
